import React from 'react';
import { useTranslation } from 'react-i18next';
import { jobOpeningPages } from '../../../../../utils/types/JobOpenings';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';

const PAGE_LABEL_KEYS: Record<string, string> = {
  'Job Opening Details': ADMIN_PAGES_KEYS.jobOpenings.pages.jobOpeningDetails,
  'Applicant Analysis': ADMIN_PAGES_KEYS.jobOpenings.pages.applicantAnalysis,
  'Auto-Assessments': ADMIN_PAGES_KEYS.jobOpenings.pages.autoAssessments,
  'Smart Interviews': ADMIN_PAGES_KEYS.jobOpenings.pages.smartInterviews,
};

interface Step {
  label: jobOpeningPages;
  status: 'active' | 'non-active' | 'disabled';
}

interface PillStyleTabsProps {
  steps: Step[];
  currentPage: jobOpeningPages;
  setCurrentPage: (page: jobOpeningPages) => void;
  counts?: Partial<Record<string, number>>;
}

export const PillStyleTabs: React.FC<PillStyleTabsProps> = ({
  steps,
  currentPage,
  setCurrentPage,
  counts = {},
}) => {
  const { t } = useTranslation(NS.adminPages);

  const availableSteps = steps.filter((step) => step.status !== 'disabled');

  const configStep = availableSteps.find(
    (s) => s.label === 'Job Opening Details',
  );
  const resultSteps = availableSteps.filter(
    (s) => s.label !== 'Job Opening Details',
  );

  const renderIcon = (label: string, isActive: boolean) => {
    const iconClass = `w-4 h-4 flex-shrink-0 transition-colors ${
      isActive ? 'text-emerald-700' : 'text-slate-500 group-hover:text-emerald-600'
    }`;

    switch (label) {
      case 'Job Opening Details':
        return (
          <svg
            className={`w-5 h-5 flex-shrink-0 transition-colors ${
              isActive ? 'text-white' : 'text-emerald-600'
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        );
      case 'Applicant Analysis':
        return (
          <svg
            className={iconClass}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        );
      case 'Auto-Assessments':
        return (
          <svg
            className={iconClass}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        );
      case 'Smart Interviews':
        return (
          <svg
            className={iconClass}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        );
      case 'Psychometric':
        return (
          <svg
            className={iconClass}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
        );
      case 'Wellness':
        return (
          <svg
            className={iconClass}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.684a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full bg-slate-50/60 border-b border-slate-200/80 p-3 overflow-x-auto">
      <div className="flex flex-wrap lg:flex-nowrap items-start gap-4 min-w-max">
        {/* Left: Job Setup (Configuration) Primary Action Pill */}
        {configStep && (() => {
          const isConfigActive = currentPage === 'Job Opening Details';
          return (
            <button
              type="button"
              onClick={() => setCurrentPage('Job Opening Details')}
              className={`group flex items-center gap-3 px-4 py-2.5 rounded-2xl transition-all duration-200 cursor-pointer shadow-sm ${
                isConfigActive
                  ? 'bg-emerald-600 text-white shadow-emerald-800/20 border border-emerald-600 ring-2 ring-emerald-600/20'
                  : 'bg-white hover:bg-emerald-50/60 text-slate-800 border border-slate-200/90 hover:border-emerald-400'
              }`}
            >
              <div
                className={`p-2 rounded-xl flex items-center justify-center transition-colors ${
                  isConfigActive
                    ? 'bg-white/20 text-white'
                    : 'bg-emerald-100/80 text-emerald-700 group-hover:bg-emerald-600 group-hover:text-white'
                }`}
              >
                {renderIcon('Job Opening Details', isConfigActive)}
              </div>
              <div className="flex flex-col items-start text-left">
                <span
                  className={`text-[13px] font-bold leading-tight ${
                    isConfigActive ? 'text-white' : 'text-slate-900'
                  }`}
                >
                  Job Setup
                </span>
                <span
                  className={`text-[10px] font-medium leading-tight ${
                    isConfigActive ? 'text-white/80' : 'text-slate-500'
                  }`}
                >
                  Configuration
                </span>
              </div>
            </button>
          );
        })()}

        {/* Right: Results Tabs Group with Bracket Line Indicator */}
        {resultSteps.length > 0 && (
          <div className="flex flex-col flex-1 gap-2 min-w-max">
            {/* Row of Result Tab Pills */}
            <div className="flex items-center gap-2.5">
              {resultSteps.map((step) => {
                const isActive = currentPage === step.label;
                const displayLabel = t(PAGE_LABEL_KEYS[step.label] ?? step.label);

                return (
                  <button
                    key={step.label}
                    type="button"
                    onClick={() => setCurrentPage(step.label)}
                    className={`group flex items-center gap-2 px-3.5 py-2 rounded-2xl text-[13px] font-semibold transition-all duration-200 cursor-pointer ${
                      isActive
                        ? 'bg-white border-2 border-emerald-600 text-emerald-950 font-bold shadow-xs ring-2 ring-emerald-500/10'
                        : 'bg-white hover:bg-slate-50 border border-slate-200/90 text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    {renderIcon(step.label, isActive)}
                    <span>{displayLabel}</span>
                  </button>
                );
              })}
            </div>

            {/* RESULTS Group Connector Bracket Line */}
            <div className="flex items-center w-full px-1">
              <div className="h-2 w-2 border-l border-b border-slate-300/90 rounded-bl-xs"></div>
              <div className="flex-1 border-b border-slate-300/90"></div>
              <span className="px-3 text-[10px] font-extrabold text-blue-700 tracking-wider uppercase">
                RESULTS
              </span>
              <div className="flex-1 border-b border-slate-300/90"></div>
              <div className="h-2 w-2 border-r border-b border-slate-300/90 rounded-br-xs"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PillStyleTabs;
